import type { Row } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useApplicationTimelineQuery } from '@access-cms/api-client';

import { formatDate } from '../../../utils';
import type { ApprovalListItem } from '../types';

/**
 * A single fine-grained lifecycle event from the application timeline. The server
 * role-scopes these (the FN approver sees events up to and including the FNMS stage) and
 * includes the per-attempt FNMS submission events (visit created, documents uploaded,
 * submission retries) — ACP-247, C4.
 */
interface TimelineEntry {
  id?: string;
  eventTitle?: string;
  eventDescription?: string | null;
  eventType?: string;
  occurredAt?: string;
}

interface ApprovalWorkflowHistoryProps {
  row: Row<ApprovalListItem>;
  'data-testid'?: string;
}

function formatEventTitle(entry: TimelineEntry): string {
  if (entry.eventTitle?.trim()) {
    return entry.eventTitle;
  }
  // Fallback: humanize the raw event type (e.g. FNMS_VISIT_CREATED → "Fnms visit created").
  const raw = entry.eventType ?? 'Event';

  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase().replaceAll('_', ' ');
}

function WorkflowHistoryShell({ baseTestId, children }: { baseTestId: string; children: ReactNode }) {
  return (
    <div
      className='approval-workflow-history'
      data-testid={baseTestId}>
      <h3
        className='approval-workflow-history__title'
        id={`${baseTestId}-heading`}
        tabIndex={0}>
        Workflow History
      </h3>
      {children}
    </div>
  );
}

function ApprovalWorkflowHistory({ row, 'data-testid': dataTestId }: ApprovalWorkflowHistoryProps) {
  const baseTestId = dataTestId ?? 'approval-workflow-history';
  const applicationId = row.original.applicationId ?? '';
  const timelineQuery = useApplicationTimelineQuery(applicationId, { enabled: !!applicationId });
  const events = (timelineQuery.data?.data ?? []) as TimelineEntry[];

  if (timelineQuery.isLoading) {
    return (
      <WorkflowHistoryShell baseTestId={baseTestId}>
        <p
          className='approval-workflow-history__empty'
          data-testid={`${baseTestId}-loading`}
          tabIndex={0}>
          Loading workflow history...
        </p>
      </WorkflowHistoryShell>
    );
  }

  if (timelineQuery.isError) {
    return (
      <WorkflowHistoryShell baseTestId={baseTestId}>
        <p
          className='approval-workflow-history__empty'
          data-testid={`${baseTestId}-error`}
          tabIndex={0}>
          Unable to load workflow history.
        </p>
      </WorkflowHistoryShell>
    );
  }

  if (events.length === 0) {
    return (
      <WorkflowHistoryShell baseTestId={baseTestId}>
        <p
          className='approval-workflow-history__empty'
          data-testid={`${baseTestId}-empty`}
          tabIndex={0}>
          No workflow history available.
        </p>
      </WorkflowHistoryShell>
    );
  }

  return (
    <WorkflowHistoryShell baseTestId={baseTestId}>
      <ul
        aria-labelledby={`${baseTestId}-heading`}
        className='approval-workflow-history__list'
        data-testid={`${baseTestId}-list`}>
        {events.map((entry, index) => (
          <li
            key={`${entry.id ?? entry.occurredAt}-${index}`}
            className='approval-workflow-history__row'
            data-testid={`${baseTestId}-row-${index}`}
            tabIndex={0}>
            <span className='approval-workflow-history__date'>{formatDate(entry.occurredAt)}</span>
            <span className='approval-workflow-history__description'>{formatEventTitle(entry)}</span>
            {entry.eventDescription?.trim() && (
              <span className='approval-workflow-history__notes'>{entry.eventDescription}</span>
            )}
          </li>
        ))}
      </ul>
    </WorkflowHistoryShell>
  );
}

export default ApprovalWorkflowHistory;
